import { Controller } from "@hotwired/stimulus"
import { SELECT_TYPES } from "./constants"
import { reportDashboardSelectItems } from "./report_dashboard_select_items"
import { closeRailsuiModal, observeModalHidden } from "./report_dashboard_modal"

const VIEW_REPORT_DISABLED_CLASSES = ["disabled", "pointer-events-none", "opacity-50"]
const SELECT_TYPE_KEYS = Object.keys(SELECT_TYPES)

export default class extends Controller {
  static targets = ["courseViewReport", "studentViewReport", "caseViewReport"]

  connect() {
    this.loadedTypes = new Set()
    this.modalObservers = []
    this.boundHiddenInputChange = this.hiddenInputChange.bind(this)
    this.element.addEventListener("change", this.boundHiddenInputChange)

    SELECT_TYPE_KEYS.forEach((type) => {
      this.menuControllerFor(type)?.setLoading(SELECT_TYPES[type].loadingMessage)
      this.updateViewReport(type)
      this.watchModalReset(type)
    })
  }

  disconnect() {
    this.element.removeEventListener("change", this.boundHiddenInputChange)
    this.modalObservers.forEach((observer) => observer.disconnect())
    this.modalObservers = []
    SELECT_TYPE_KEYS.forEach((type) => this.reset(type))
  }

  hiddenInputChange(event) {
    const input = event.target
    if (!input.matches('[data-railsui-dropdown-menu-target="hiddenInput"]')) return

    const type = input.closest("[data-controller~='railsui-dropdown-menu']")?.dataset.reportDashboardDsDropdownType
    if (type) this.updateViewReport(type)
  }

  async load(type) {
    const config = SELECT_TYPES[type]
    const menuController = this.menuControllerFor(type)
    if (!config || !menuController || this.loadedTypes.has(type)) return

    menuController.setLoading(config.loadingMessage)

    try {
      const response = await fetch(config.fetchUrl)
      if (!response.ok) throw new Error(`Failed to load ${type}`)

      const items = await response.json()

      if (Array.isArray(items) && items.length > 0) {
        menuController.setOptions(reportDashboardSelectItems(type, items))
      } else {
        menuController.setMessage(config.emptyMessage)
      }

      this.loadedTypes.add(type)
    } catch {
      menuController.setMessage(config.errorMessage)
      this.loadedTypes.delete(type)
    }

    this.updateViewReport(type)
  }

  updateViewReport(type) {
    const config = SELECT_TYPES[type]
    const viewReport = this.viewReportFor(type)
    const hiddenInput = this.hiddenInputFor(type)
    if (!config || !viewReport || !hiddenInput) return

    const selectedId = hiddenInput.value

    viewReport.setAttribute("aria-label", config.viewReportLabel)

    if (selectedId) {
      viewReport.disabled = false
      viewReport.onclick = (event) => {
        event.preventDefault()
        this.closeModal(type)
        window.location.assign(this.reportUrl(type, selectedId))
      }
      viewReport.classList.remove(...VIEW_REPORT_DISABLED_CLASSES)
      viewReport.removeAttribute("aria-disabled")
      return
    }

    viewReport.disabled = true
    viewReport.onclick = null
    viewReport.classList.add(...VIEW_REPORT_DISABLED_CLASSES)
    viewReport.setAttribute("aria-disabled", "true")
  }

  reset(type) {
    this.menuControllerFor(type)?.clearSelections({ submit: false })
    this.updateViewReport(type)
  }

  reportUrl(type, selectedId) {
    const config = SELECT_TYPES[type]
    if (!config) return ""

    if (config.reportUrl) return config.reportUrl(selectedId)

    const urlTemplate = document.getElementById("course-report-url")?.value || ""
    return urlTemplate.replace("{course_id}", selectedId)
  }

  closeModal(type) {
    const modalId = SELECT_TYPES[type]?.modalId
    if (!modalId) return

    closeRailsuiModal(this.application, modalId, this.element)
  }

  watchModalReset(type) {
    const modalId = SELECT_TYPES[type]?.modalId
    if (!modalId) return

    const observer = observeModalHidden(modalId, this.element, () => this.reset(type))
    if (observer) this.modalObservers.push(observer)
  }

  menuControllerFor(type) {
    const shell = this.dropdownShellFor(type)
    if (!shell) return null

    return this.application.getControllerForElementAndIdentifier(shell, "railsui-dropdown-menu")
  }

  dropdownShellFor(type) {
    return this.element.querySelector(`[data-report-dashboard-ds-dropdown-type="${type}"]`)
  }

  hiddenInputFor(type) {
    return this.dropdownShellFor(type)?.querySelector('[data-railsui-dropdown-menu-target="hiddenInput"]')
  }

  viewReportFor(type) {
    const targetName = `${type}ViewReport`
    const hasTarget = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`

    return this[hasTarget] ? this[`${targetName}Target`] : null
  }
}
